// Helper function to find all instances of a value in the list (case-insensitive, exact match)
export const findAllInstancesByValue = (
  searchValue: string,
  list: any[],
): Array<{ parentKey: string; childKey: string; childValue: string }> => {
  const results: Array<{
    parentKey: string;
    childKey: string;
    childValue: string;
  }> = [];
  const normalizedSearch = searchValue.toLowerCase().trim();

  list.forEach((parent) => {
    if (parent.value?.toLowerCase().trim() === normalizedSearch) {
      results.push({
        parentKey: parent.key,
        childKey: parent.key,
        childValue: parent.value,
      });
    }

    if (parent.children?.length > 0) {
      parent.children.forEach((child: any) => {
        if (child.value?.toLowerCase().trim() === normalizedSearch) {
          results.push({
            parentKey: parent.key,
            childKey: child.key,
            childValue: child.value,
          });
        }
      });
    }
  });

  return results;
};

// Helper function to perform local search on hierarchical data structures
export const performLocalSearch = (
  searchTerm: string,
  dataObject: Array<{
    key: string;
    value: string;
    children?: Array<{ key: string; value: string }>;
  }>,
  maxResults = 15,
): string[] => {
  const normalizedSearch = searchTerm.toLowerCase().trim();
  const results: string[] = [];
  const seen = new Set<string>();

  if (!normalizedSearch || !dataObject || dataObject.length === 0) {
    return results;
  }

  let i = 0;
  while (i < dataObject.length && results.length < maxResults) {
    const parent = dataObject[i];
    
    if (parent.value && parent.value.toLowerCase().includes(normalizedSearch)) {
      if (!seen.has(parent.value)) {
        results.push(parent.value);
        seen.add(parent.value);
        if (results.length >= maxResults) {
          return results;
        }
      }
    }

    if (parent.children && parent.children.length > 0) {
      let j = 0;
      while (j < parent.children.length && results.length < maxResults) {
        const child = parent.children[j];
        if (child.value && child.value.toLowerCase().includes(normalizedSearch)) {
          if (!seen.has(child.value)) {
            results.push(child.value);
            seen.add(child.value);
          }
        }
        j += 1;
      }
    }
    i += 1;
  }

  return results;
};

export const calculateCheckState = ({
  checkedChild,
  totalChild,
}: {
  checkedChild: number;
  totalChild: number;
}): boolean | 'indeterminate' => {
  if (checkedChild === 0) return false;
  if (checkedChild > 0 && checkedChild < totalChild) return 'indeterminate';
  return true;
};

export const getCheckedKeys = (
  obj: any,
  include = true,
  showExactMatch?: boolean,
): string[] => {
  let result = [];
  
  Object.values(obj).forEach((item: any) => {
    const shouldInclude = include && item?.checked === true && !item?.isExcluded;
    const shouldExclude = !include && item?.checked === true && item?.isExcluded;

    if (shouldInclude || shouldExclude) {
      result.push(item?.key);
      if (showExactMatch && item?.children) {
        result = result.concat(
          getCheckedKeys(item.children, true),
          getCheckedKeys(item.children, false),
        );
      }
    } else if (item?.children && item?.checked === 'indeterminate') {
      result = result.concat(getCheckedKeys(item.children, include));
    } else if (item?.checked === false) {
      const checkedChildren = item?.children?.filter(
        (child) => child?.checked === true,
      );
      if (checkedChildren?.length) {
        result = result.concat(getCheckedKeys(checkedChildren, include));
      }
    }
  });
  
  return result;
};

export type CompanySuggestion = {
  companyId: number;
  companyName: string;
  companyDomain: string;
  companyWebsite: string;
  companyLogoUrl: string;
};

export type SuggestionItem =
  | string
  | {
      value: string;
      type: string;
      emoji: string;
    }
  | CompanySuggestion;

export const isV2Suggestion = (
  suggestion: SuggestionItem,
): suggestion is { value: string; type: string; emoji: string } =>
  typeof suggestion !== 'string' && 'emoji' in suggestion;

export const isCompanySuggestion = (
  suggestion: SuggestionItem,
): suggestion is CompanySuggestion =>
  typeof suggestion !== 'string' && 'companyId' in suggestion;

export const getSuggestionValue = (suggestion: SuggestionItem): string => {
  if (typeof suggestion === 'string') return suggestion;
  if (isCompanySuggestion(suggestion)) return suggestion.companyName;
  return suggestion.value;
};

export const getSuggestionKey = (suggestion: SuggestionItem, index: number): string => {
  if (typeof suggestion === 'string') return suggestion;
  if (isCompanySuggestion(suggestion)) return String(suggestion.companyId);
  return `${suggestion.value}-${suggestion.type}-${index}`;
};

